import {
  animate,
  AnimationEvent,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import {
  isAuthorizedInKeycloak,
  isStaffAuthorizedInKeycloak,
} from '../../../keycloak/reducers/keycloak.reducer';
import {
  listClosed,
  openList,
  setAvailableListPending,
} from '../../actions/list.actions';
import {
  BookmarksSearchFacets,
  BookmarksSearchType,
  ListWithItemsCount,
  StepDisplayedEnum,
} from '../../models/list';
import { getLists, selectBookmarksAvailableListPending } from '../../reducers/list.reducer';
import { ListSearchService } from 'app/list/services/list-search.service';

@Component({
  selector: "app-searchable-bookmarks",
  templateUrl: "./searchable-bookmarks.component.html",
  styleUrls: ["./searchable-bookmarks.component.scss"],
  animations: [
    trigger("openClose", [
      state("true", style({ left: "-100%" })),
      state("false", style({ left: "0%" })),
      transition("true <=> false", animate("0.5s ease-out")),
    ]),
  ],
})
export class SearchableBookmarksComponent implements OnInit, OnDestroy {
  public lists: ListWithItemsCount[] = [];
  public emptyLists: ListWithItemsCount[] = [];
  public stepDisplayed: StepDisplayedEnum = StepDisplayedEnum.lists;
  public listOpenCloseState = false;
  public authorized: boolean;
  public isStaffAuthorized: boolean;
  public facets: BookmarksSearchFacets;
  public count: number;
  public availableCount: number;

  public readonly stepDisplayedEnum = StepDisplayedEnum;
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly listSearchService: ListSearchService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.subscriptions.add(
      combineLatest([
        this.store.select(isAuthorizedInKeycloak),
        this.store.select(isStaffAuthorizedInKeycloak),
        this.store.select(getLists),
      ]).subscribe(([authorized, staffAuthorized, lists]) => {
        this.authorized = authorized || staffAuthorized;
        this.isStaffAuthorized = staffAuthorized;
        if (this.authorized && lists.length) {
          this.lists = JSON.parse(JSON.stringify(lists));
          const emptyLists = (
            JSON.parse(JSON.stringify(lists)) as ListWithItemsCount[]
          ).map((l) => ({ ...l, items: [] }));
          emptyLists.sort(this.orderLists);
          this.emptyLists = emptyLists;
          const availableSearch = {
            text: '',
            availableOnly: false,
            type: BookmarksSearchType.Author,
          };
          this.listSearchService
            .searchAll(availableSearch, true)
            .subscribe(({ initialCount, initialAvailableCount }) => {
              this.count = initialCount;
              this.availableCount = initialAvailableCount;
              this.cdr.detectChanges();
            });
        }
      })
    );

    this.subscriptions.add(this.store.select(selectBookmarksAvailableListPending).subscribe((isPending) => {
      if (isPending) {
        setTimeout(() => this.openAvailableList());
      }
      this.store.dispatch(setAvailableListPending({isPending: false}));
    }));
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public onOpenCloseAnimation(event: AnimationEvent): void {}

  public closeList() {
    this.stepDisplayed = this.stepDisplayedEnum.lists;
    this.listOpenCloseState = false;
    this.store.dispatch(listClosed());
  }

  public openList(list: ListWithItemsCount) {
    this.stepDisplayed = this.stepDisplayedEnum.singleList;
    this.listOpenCloseState = true;
    this.store.dispatch(openList({ listId: list.id }));
  }

  public openAvailableList() {
    this.stepDisplayed = this.stepDisplayedEnum.availableList;
    this.listOpenCloseState = true;
  }

  private orderLists(a: ListWithItemsCount, b: ListWithItemsCount) {
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    }
    return 0;
  }
}
